import type { LecturaFichaObra } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { Icono } from '../componentes/Iconos.js';
import type { Ficha, FichasDeCampo as Taco } from './useFichasDeCampo.js';

/**
 * LA RUTINA DIARIA: LAS FICHAS DE PAPEL ENTRAN POR LA FOTO.
 *
 * Quien recorre las obras vuelve con un taco de fichas rellenadas a mano.
 * Sentarse a teclear treinta fichas es el trabajo que nadie hace, y por eso las
 * fichas se quedan en la carpeta y la cartera no crece.
 *
 * Se sueltan aquí las fotos del día, se leen una a una y sale un BORRADOR por
 * ficha. NO SE DA DE ALTA NADA: cada borrador se abre en el formulario de
 * siempre, se repasa y se guarda a mano. Un lector automático se equivoca —un
 * teléfono con un dígito mal es una llamada a un desconocido— y el repaso es
 * justo lo que no se puede quitar.
 *
 * Lo que sí se quita es tener que repasarlo TODO: cada tarjeta dice qué campos
 * ha sacado del papel y qué no ha entendido.
 *
 * EL TACO NO VIVE AQUÍ, vive en el módulo (`useFichasDeCampo`): entrar a
 * repasar una ficha desmonta esta pantalla, y con el taco dentro las demás
 * fichas desaparecían al volver. Aquí sólo se enseñan.
 */

interface Props {
  idioma: Idioma;
  taco: Taco;
  onRevisar: (ficha: Ficha, lectura: LecturaFichaObra) => void;
  onVolver: () => void;
}

/** Los nombres de campo que devuelve el lector, con su etiqueta de pantalla. */
const ETIQUETA_CAMPO: Record<string, string> = {
  nombre: 'obras.campoNombre',
  direccion: 'obras.campoDireccion',
  codigoPostal: 'obras.campoCp',
  municipio: 'obras.campoMunicipio',
  zona: 'obras.campoZona',
  tipoProyecto: 'obras.campoTipo',
  fase: 'obras.campoFase',
  progreso: 'obras.campoProgreso',
  finalizacionEstimada: 'obras.campoFin',
  confianza: 'obras.campoConfianza',
  comentarioObra: 'obras.campoComentario',
  etapa: 'obras.campoEtapa',
  responsable: 'obras.campoResponsable',
  promotor: 'obras.promotor',
  valorMercado: 'obras.campoValor',
  contactos: 'obras.campoContactos',
};

export function FichasDeCampo({ idioma, taco, onRevisar, onVolver }: Props) {
  const { fichas, leyendo: pendientes } = taco;

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'obras.fichasTitulo')}</h1>
          <p>{t(idioma, 'obras.fichasSub')}</p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      <section className="tarjeta">
        <label className="campo">
          <span className="etiqueta-campo">{t(idioma, 'obras.fichasSubir')}</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              taco.anadir(e.target.files);
              // Se limpia para poder volver a elegir el mismo fichero: si no,
              // repetir una foto que fallo no dispara el evento.
              e.target.value = '';
            }}
          />
        </label>
        <p className="suave">{t(idioma, 'obras.fichasNota')}</p>
      </section>

      {pendientes > 0 && (
        <p className="suave" role="status">
          {t(idioma, 'obras.fichasLeyendo', { total: pendientes })}
        </p>
      )}

      <div className="fichas-leidas">
        {fichas.map((ficha) => (
          <section key={ficha.id} className="tarjeta ficha-leida">
            <img src={ficha.vista} alt={ficha.fichero.name} />
            <div className="ficha-leida-cuerpo">
              <b>{ficha.fichero.name}</b>

              {ficha.estado.fase === 'leyendo' && (
                <p className="suave">{t(idioma, 'obras.fichaLeyendo')}</p>
              )}

              {ficha.estado.fase === 'fallo' && (
                <p className="error" role="alert">
                  {ficha.estado.motivo}
                </p>
              )}

              {ficha.estado.fase === 'leida' && (
                <Leida
                  idioma={idioma}
                  lectura={ficha.estado.lectura}
                  onRevisar={() => {
                    if (ficha.estado.fase === 'leida') {
                      onRevisar(ficha, ficha.estado.lectura);
                    }
                  }}
                />
              )}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function Leida({
  idioma,
  lectura,
  onRevisar,
}: {
  idioma: Idioma;
  lectura: LecturaFichaObra;
  onRevisar: () => void;
}) {
  const titulo = lectura.obra.nombre || lectura.obra.direccion || t(idioma, 'obras.fichaSinNombre');
  return (
    <>
      <p className="ficha-leida-titulo">{titulo}</p>

      {/* Qué ha salido del papel. Sin esto, repasar es mirarlo todo otra vez. */}
      <p className="suave">
        {t(idioma, 'obras.fichaCamposLeidos', { total: lectura.camposLeidos.length })}
      </p>
      <div className="ficha-campos">
        {lectura.camposLeidos.map((campo) => (
          <span key={campo} className="chip-area">
            {ETIQUETA_CAMPO[campo] ? t(idioma, ETIQUETA_CAMPO[campo]) : campo}
          </span>
        ))}
      </div>

      {/*
        LO QUE NO SE HA ENTENDIDO VA DELANTE, no escondido en un desplegable:
        es lo único de la tarjeta que obliga a mirar el papel otra vez.
      */}
      {lectura.dudas.length > 0 && (
        <div className="ficha-dudas">
          <b>{t(idioma, 'obras.fichaDudas')}</b>
          <ul>
            {lectura.dudas.map((duda) => (
              <li key={duda}>{duda}</li>
            ))}
          </ul>
        </div>
      )}

      <button type="button" onClick={onRevisar}>
        {t(idioma, 'obras.fichaRevisar')}
      </button>
    </>
  );
}
